import React from "react"
import {
	Container,
	Box,
	Accordion as MUIAccordion,
	AccordionSummary as MUIAccordionSummary,
	AccordionDetails as MUIAccordionDetails,
} from "@mui/material"
import { makeStyles } from "makeStyles"
import Icon from "@mdi/react"
import { mdiChevronDown } from "@mdi/js"

import Ratings from "./Ratings"
import TechnicalDetails from "./TechnicalDetails"
import StickyBTFNavigation from "../StickyBTFNavigation"
import Assets from "./Assets"

import { ProductDetailPageReducerState } from "@secureo/common/reducers/productDetailPageReducer"
import { ReviewRatingStatistics, Review } from "@secureo/common/typings/Product"

const useStyles = makeStyles()({
	responsiveWidth: {
		maxWidth: 950,
		"@media only screen and (max-width: 1260px)": {
			maxWidth: "100%",
		},
	},
	paddingForAnchors: {
		paddingTop: 20,
		paddingBottom: 20,
		"@media only screen and (max-width: 800px)": {
			paddingTop: 0,
			paddingBottom: 0,
		},
	},
	accHeader: {
		borderLeft: "3px solid #4691CE",
		background: "none",
		"@media only screen and (max-width: 800px)": {
			borderLeft: "none",
		},
	},
	accBackground: {
		background: "#E7EFF6",
	},
})

const Accordion = ({
	accessToken,
	productId,
	masterVariantSku,
	reducerState,
	reviewRatingStatistics,
	reviews,
	setQuantity,
	addToShoppingList,
	addToCart,
}: Props) => {
	const { classes } = useStyles()

	const {
		description,
		isLoading,
		attributes,
		quantity,
		singleGrossPriceCents,
		singleGrossMsrpPriceCents,
		assets,
	} = reducerState

	const { technicalDetails = {}, manufacturer } = attributes

	return (
		<Box mt={5}>
			<StickyBTFNavigation
				singleGrossPriceCents={singleGrossPriceCents}
				singleGrossMsrpPriceCents={singleGrossMsrpPriceCents}
				addToCart={addToCart}
				isLoading={isLoading}
				quantity={quantity}
				setQuantity={setQuantity}
				hasTechnicalDetails={Object.keys(technicalDetails).length > 0}
				hasAssets={Object.keys(assets).length > 0}
				hasDescription={!!description}
				addToShoppingList={addToShoppingList}
				manufacturer={manufacturer}
			/>

			{description && (
				<Box className={classes.paddingForAnchors} id="description">
					<Container id="description">
						<Box className={classes.responsiveWidth}>
							<Box className={classes.paddingForAnchors}>
								<MUIAccordion
									style={{ boxShadow: "none", background: "none" }}
									defaultExpanded
								>
									<MUIAccordionSummary
										id="description"
										expandIcon={<Icon path={mdiChevronDown} size={1} />}
										aria-controls="panel1a-content"
										className={classes.accHeader}
									>
										<h3 style={{ margin: 0 }}>
											<strong>Beschreibung</strong>
										</h3>
									</MUIAccordionSummary>
									<MUIAccordionDetails>
										<div
											dangerouslySetInnerHTML={{ __html: description }}
											style={{ overflow: "hidden", color: "#505e5f" }}
										/>
									</MUIAccordionDetails>
								</MUIAccordion>
							</Box>
						</Box>
					</Container>
				</Box>
			)}
			{Object.keys(technicalDetails).length > 0 && (
				<Box
					className={`${classes.paddingForAnchors} ${classes.accBackground}`}
					id="technical-details"
				>
					<Container>
						<Box className={classes.responsiveWidth}>
							<MUIAccordion
								style={{ boxShadow: "none", background: "none" }}
								defaultExpanded
							>
								<MUIAccordionSummary
									id="technicalDetails"
									expandIcon={<Icon path={mdiChevronDown} size={1} />}
									aria-controls="panel1a-content"
									className={classes.accHeader}
								>
									<h3 style={{ margin: 0 }}>
										<strong>Technische Daten</strong>
									</h3>
								</MUIAccordionSummary>
								<MUIAccordionDetails>
									<TechnicalDetails technicalDetails={technicalDetails} />
								</MUIAccordionDetails>
							</MUIAccordion>
						</Box>
					</Container>
				</Box>
			)}
			<Box className={classes.paddingForAnchors} id="ratings">
				<Container>
					<Box className={classes.responsiveWidth}>
						<MUIAccordion
							style={{ boxShadow: "none", background: "none" }}
							defaultExpanded
						>
							<MUIAccordionSummary
								id="ratings"
								expandIcon={<Icon path={mdiChevronDown} size={1} />}
								aria-controls="panel1a-content"
								className={classes.accHeader}
							>
								<h3 style={{ margin: 0 }}>
									<strong>Bewertungen</strong>
								</h3>
							</MUIAccordionSummary>
							<MUIAccordionDetails>
								<Ratings
									accessToken={accessToken}
									productId={productId}
									masterVariantSku={masterVariantSku}
									reviewRatingStatistics={reviewRatingStatistics}
									reviews={reviews}
								/>
							</MUIAccordionDetails>
						</MUIAccordion>
					</Box>
				</Container>
			</Box>
			{assets.length > 0 && (
				<Box className={`${classes.paddingForAnchors} ${classes.accBackground}`} id="media">
					<Container>
						<Box className={classes.responsiveWidth}>
							<MUIAccordion
								style={{ boxShadow: "none", background: "none" }}
								defaultExpanded
							>
								<MUIAccordionSummary
									id="media"
									expandIcon={<Icon path={mdiChevronDown} size={1} />}
									aria-controls="panel1a-content"
									className={classes.accHeader}
								>
									<h3 style={{ margin: 0 }}>
										<strong>Dokumente &amp; Links</strong>
									</h3>
								</MUIAccordionSummary>
								<MUIAccordionDetails>
									<Assets assets={assets} />
								</MUIAccordionDetails>
							</MUIAccordion>
						</Box>
					</Container>
				</Box>
			)}
		</Box>
	)
}

interface Props {
	accessToken: string
	productId: string
	masterVariantSku: string
	reducerState: ProductDetailPageReducerState
	reviewRatingStatistics: ReviewRatingStatistics
	reviews: Review[]
	setQuantity: (quantity: number) => void
	addToShoppingList: () => void
	addToCart: () => void
}

export default Accordion
